import React, { createContext, useContext, useMemo } from 'react'
import { AdvancedSettingsProvider } from './AdvancedSettingsProvider'
import { CaseloadState, useAdvancedCaseloadFetch } from './useAdvancedCaseloadFetch'
import { AdvancedPatientSummary } from './types'

const AdvancedCaseloadContext = createContext<CaseloadState>({ status: 'disabled' })
const RefreshCaseloadContext = createContext<() => void>(() => undefined)

export const AdvancedCaseloadProvider = ({ children }: { children: React.ReactNode }) => {
  const { caseload, refresh } = useAdvancedCaseloadFetch()

  return (
    <AdvancedSettingsProvider>
      <RefreshCaseloadContext.Provider value={refresh}>
        <AdvancedCaseloadContext.Provider value={caseload}>{children}</AdvancedCaseloadContext.Provider>
      </RefreshCaseloadContext.Provider>
    </AdvancedSettingsProvider>
  )
}

export const useAdvancedCaseload = () => useContext(AdvancedCaseloadContext)

export const useRefreshAdvancedCaseload = () => useContext(RefreshCaseloadContext)

// An error keeps the switch: the screen naming the fault sits behind it (ADR-068).
export const useShouldShowAdvancedModeSwitch = (): boolean => {
  const caseload = useAdvancedCaseload()

  if (caseload.status === 'ready') {
    return Object.keys(caseload.byUid).length > 0
  }

  return caseload.status === 'error'
}

export type AdvancedPatientCheck =
  | { status: 'undetermined' }
  | { status: 'advanced'; summary: AdvancedPatientSummary }
  | { status: 'standard' }

export const useAdvancedPatientCheck = (uid: string | undefined): AdvancedPatientCheck => {
  const caseload = useAdvancedCaseload()

  return useMemo<AdvancedPatientCheck>(() => {
    if (!uid) {
      return { status: 'standard' }
    }

    if (caseload.status === 'loading') {
      return { status: 'undetermined' }
    }

    if (caseload.status !== 'ready') {
      return { status: 'standard' }
    }

    const summary = caseload.byUid[uid]

    return summary ? { status: 'advanced', summary } : { status: 'standard' }
  }, [caseload, uid])
}
